<template>
  <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700 overflow-hidden">
    <div class="p-6">
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <p class="text-sm font-medium text-secondary-600 dark:text-secondary-400">
            {{ title }}
          </p>
          <div class="mt-2 flex items-center">
            <component :is="statusIcon" :class="[statusColor, 'h-6 w-6 mr-2']" />
            <span :class="[statusColor, 'text-lg font-semibold']">
              {{ statusText }}
            </span>
          </div>
        </div>
        <div :class="[iconBgColor, 'p-3 rounded-lg']">
          <component :is="iconComponent" :class="[iconColor, 'h-8 w-8']" />
        </div>
      </div>

      <!-- Additional Metrics -->
      <div class="mt-4 space-y-2">
        <div v-if="responseTime !== undefined" class="flex items-center justify-between text-sm">
          <span class="text-secondary-600 dark:text-secondary-400">Response Time</span>
          <span class="font-medium text-secondary-900 dark:text-white">{{ responseTime }}ms</span>
        </div>
        <div v-if="hitRate !== undefined" class="flex items-center justify-between text-sm">
          <span class="text-secondary-600 dark:text-secondary-400">Hit Rate</span>
          <span class="font-medium text-secondary-900 dark:text-white">{{ hitRate }}%</span>
        </div>
        <div v-if="jobsProcessed !== undefined" class="flex items-center justify-between text-sm">
          <span class="text-secondary-600 dark:text-secondary-400">Jobs Processed</span>
          <span class="font-medium text-secondary-900 dark:text-white">{{ jobsProcessed }}</span>
        </div>
        <div v-if="usedSpace !== undefined" class="flex items-center justify-between text-sm">
          <span class="text-secondary-600 dark:text-secondary-400">Used Space</span>
          <span class="font-medium text-secondary-900 dark:text-white">{{ usedSpace }}</span>
        </div>
      </div>
    </div>

    <!-- Status bar -->
    <div :class="[statusBarColor, 'h-1']"></div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import {
  CircleStackIcon,
  CpuChipIcon,
  QueueListIcon,
  ServerIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  status: {
    type: String,
    required: true,
    validator: (value) => ['healthy', 'warning', 'critical'].includes(value),
  },
  responseTime: {
    type: [Number, String],
    default: undefined,
  },
  hitRate: {
    type: [Number, String],
    default: undefined,
  },
  jobsProcessed: {
    type: [Number, String],
    default: undefined,
  },
  usedSpace: {
    type: String,
    default: undefined,
  },
});

const iconMap = {
  'Database': CircleStackIcon,
  'Cache': CpuChipIcon,
  'Queue': QueueListIcon,
  'Storage': ServerIcon,
};

const iconComponent = computed(() => {
  return iconMap[props.title] || ServerIcon;
});

const statusIcon = computed(() => {
  const icons = {
    healthy: CheckCircleIcon,
    warning: ExclamationTriangleIcon,
    critical: XCircleIcon,
  };
  return icons[props.status] || CheckCircleIcon;
});

const statusText = computed(() => {
  const texts = {
    healthy: 'Healthy',
    warning: 'Warning',
    critical: 'Critical',
  };
  return texts[props.status] || 'Unknown';
});

const statusColor = computed(() => {
  const colors = {
    healthy: 'text-success-600 dark:text-success-400',
    warning: 'text-warning-600 dark:text-warning-400',
    critical: 'text-danger-600 dark:text-danger-400',
  };
  return colors[props.status] || 'text-secondary-600';
});

const statusBarColor = computed(() => {
  const colors = {
    healthy: 'bg-success-600',
    warning: 'bg-warning-600',
    critical: 'bg-danger-600',
  };
  return colors[props.status] || 'bg-secondary-600';
});

const iconColor = computed(() => {
  const colors = {
    healthy: 'text-success-600 dark:text-success-400',
    warning: 'text-warning-600 dark:text-warning-400',
    critical: 'text-danger-600 dark:text-danger-400',
  };
  return colors[props.status] || 'text-secondary-600';
});

const iconBgColor = computed(() => {
  const colors = {
    healthy: 'bg-success-100 dark:bg-success-900/30',
    warning: 'bg-warning-100 dark:bg-warning-900/30',
    critical: 'bg-danger-100 dark:bg-danger-900/30',
  };
  return colors[props.status] || 'bg-secondary-100';
});
</script>
